import { fillText } from 'components/invoiceImport/invoiceImportText';
import { formatEuros } from './invoiceAmount';

/**
 * The cause of each error code an invoice can carry (api
 * utils/invoiceImportErrors.js, INVOICE_ERROR_STEPS), read after
 * "Import échoué : ". The two codes that stop a whole run never land on an
 * invoice: the import toast words them.
 */
export const INVOICE_ERROR_CAUSES = {
  ERR_BESTT_FACTURE_INTROUVABLE: 'Bestt ne connaît pas cette facture',
  ERR_BESTT_API: 'Bestt a échoué sur cette facture',
  ERR_ENTREPRISE_INCONNUE_JOBBIZ:
    'aucune entreprise Jobbiz n’a ce client Bestt',
  ERR_ENTREPRISE_AMBIGUE_JOBBIZ:
    'plusieurs entreprises Jobbiz ont ce client Bestt',
  ERR_BESTT_PDF_INDISPONIBLE: 'Bestt n’a pas rendu le PDF',
  ERR_STOCKAGE_PDF: 'le PDF n’a pas pu être rangé ou relu',
  ERR_PENNYLANE_DESACTIVE: 'hors production, rien n’est envoyé à Pennylane',
  ERR_ENTREPRISE_INTROUVABLE_PENNYLANE:
    'aucun client Pennylane pour cette entreprise',
  ERR_ENTREPRISE_AMBIGUE_PENNYLANE:
    'plusieurs clients Pennylane pour ce SIRET ou ce SIREN',
  ERR_FACTURE_AMBIGUE_PENNYLANE:
    'plusieurs versions de cette facture dans Pennylane, ou seulement des archivées',
  ERR_TVA_TAUX_INCONNU: 'taux de TVA inconnu',
  ERR_PENNYLANE_API: 'Pennylane a refusé la facture ou n’a pas répondu',
};

/** The codes whose import did not fail: a sentence of their own. */
export const INVOICE_ERROR_SENTENCES = {
  ERR_PENNYLANE_CATEGORIE:
    'Facture importée, mais sa catégorie n’est pas posée.',
  ERR_PENNYLANE_ECHEANCE:
    'Facture importée, mais son échéance n’est pas posée dans Pennylane.',
  ERR_PENNYLANE_ANNEXE:
    'Facture importée, mais une annexe n’est pas envoyée dans Pennylane.',
};

const isOwnKey = (table, key) =>
  Object.prototype.hasOwnProperty.call(table, key);

/**
 * "31/07/2026", the day in Paris. The api stores a due date at midnight UTC:
 * read in the browser's own zone, it would show the day before west of UTC.
 */
export const formatParisDay = (value) => {
  if (value == null) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Paris',
  }).format(date);
};

const isAmount = (value) => typeof value === 'number' && Number.isFinite(value);

// What is paid and what is left are null until the payment sync reads them:
// a sentence never shows a missing amount.
const partiallyPaidText = (text, { paidAmount, remainingAmount }) => {
  const paid = isAmount(paidAmount) ? formatEuros(paidAmount) : null;
  const remaining = isAmount(remainingAmount)
    ? formatEuros(remainingAmount)
    : null;
  if (paid && remaining) {
    return fillText(
      text('partiallyPaid', '{paid} réglés · reste {remaining} à payer.'),
      { paid, remaining },
    );
  }
  if (paid) {
    return fillText(text('partiallyPaidPaidOnly', '{paid} réglés.'), { paid });
  }
  if (remaining) {
    return fillText(
      text('partiallyPaidRemainingOnly', 'Reste {remaining} à payer.'),
      { remaining },
    );
  }
  return text('partiallyPaidUnknown', 'Réglée en partie.');
};

const paidText = (text, { paidAt }) => {
  const date = formatParisDay(paidAt);
  // Invoices paid before the sync, or without a settlement Pennylane dates,
  // have no date.
  return date
    ? fillText(text('paidOn', 'Réglée en totalité le {date}.'), { date })
    : text('paid', 'Réglée en totalité.');
};

const errorText = (text, { errorCode }) => {
  if (isOwnKey(INVOICE_ERROR_SENTENCES, errorCode)) {
    return text(
      `errorSentences.${errorCode}`,
      INVOICE_ERROR_SENTENCES[errorCode],
    );
  }
  // Own keys only: "constructor" is no cause.
  if (!isOwnKey(INVOICE_ERROR_CAUSES, errorCode)) {
    return text('errorUnknown', 'Import échoué.');
  }
  const cause = text(
    `errorCauses.${errorCode}`,
    INVOICE_ERROR_CAUSES[errorCode],
  );
  return fillText(text('error', 'Import échoué : {cause}.'), { cause });
};

const STATUS_TEXTS = {
  not_imported: (text) =>
    text(
      'notImported',
      'Pas encore récupérée depuis Bestt ni envoyée dans Pennylane.',
    ),
  draft: (text) =>
    text(
      'draft',
      'Importée dans Pennylane en brouillon, pas encore finalisée.',
    ),
  pending: (text) =>
    text('pending', 'Finalisée dans Pennylane, en attente de paiement.'),
  partially_paid: partiallyPaidText,
  paid: paidText,
  cancelled: (text) =>
    text(
      'cancelled',
      'Annulée dans Pennylane, exclue des montants à encaisser.',
    ),
  error: errorText,
};

/**
 * The tooltip of the status badge (JBWEB-1272), or null for a status the list
 * does not know. `text(key, fallback)` reads invoiceList.statusTooltip.
 */
export const statusTooltipText = (text, invoice) => {
  // Own keys only: a "toString" status has no tooltip.
  if (!isOwnKey(STATUS_TEXTS, invoice.status)) return null;
  return STATUS_TEXTS[invoice.status](text, invoice);
};

/** The tooltip of the "En retard · N j" badge. */
export const lateTooltipText = (text, { dueDate }) => {
  const date = formatParisDay(dueDate);
  return date
    ? fillText(text('late', 'Échéance du {date} dépassée.'), { date })
    : text('lateNoDate', 'Échéance dépassée.');
};
